import React, {useEffect, useContext, useState} from "react";
import axios from "@common/axios";
import {UserContext} from "@context/UserContext";
import ButtonsGroup from "@common/ButtonsGroup";
import Button from "@common/Button";
import useCurrentShortcut from "@hooks/useCurrentShortcut";
import useCurrentConcept from "@hooks/useCurrentConcept";
import {BrandContext} from "@context/BrandContext";
import CircularLoader from "@common/CircularLoader";
import {NavigationContext} from "@common/context/NavigationContext";

const StudentDiagnosticMenuWidget = props => {
    const {user, updateFollowUpWork} = useContext(UserContext);
    const brandConfig = useContext(BrandContext);
    const { setNavVisible } = useContext(NavigationContext)
    const [skillResources, setSkillResources] = useState([]);
    const [isLoading, setLoading] = useState(true);
    const [activeSkillResource, setActiveSkillResource] = useState(0);
    const {loadShortcutByUrl} = useCurrentShortcut();
    const {loadCurrentConcept} = useCurrentConcept();

    function handlePrevClick() {
        if (activeSkillResource > 0) setActiveSkillResource(activeSkillResource - 1);
    }

    function handleNextClick() {
        if (activeSkillResource < (skillResources.length - 1)) setActiveSkillResource(activeSkillResource + 1);
    }

    function handleViewAll() {
        setNavVisible(false);
        loadShortcutByUrl("/web/ui/view/diagnostics/studentReports", null, user.testResultId);
    }

    useEffect(() => {
        if (user.testResultId) {
            setLoading(true);
            axios.get('/web/ui/test/viewStudentDiagnosticTestFeedback', {params: {testResultId: user.testResultId}})
                .then(response => {
                    const newSkillResources = [];
                    Object.keys(response.data.skillResources).forEach((skillId, index) => {
                        let newSkillResource = {
                            key: (index + 1),
                        };
                        newSkillResource.skillId = skillId;
                        newSkillResource.name = response.data.testResultSkills.filter(obj => obj.skillId === parseInt(skillId))[0].name;
                        newSkillResource.skills = {};
                        response.data.skillResources[skillId].map((skillResource) => {
                            const newSkill = {};
                            if (skillResource.resourceType === "TUTORIAL") {
                                newSkillResource.skills.textbook = {
                                    type: "textbook",
                                    name: skillResource.textbookConceptName,
                                    conceptId: skillResource.conceptId,
                                    tabHash: skillResource.tabHash
                                };
                            } else if (skillResource.resourceType === "QUESTION_CLUSTER") {
                                newSkillResource.skills.questions = {
                                    type: "questions",
                                    name: skillResource.resourceSubNumbers,
                                    conceptId: skillResource.conceptId,
                                    tabHash: skillResource.tabHash
                                };
                            } else if (skillResource.resourceType === "QUESTION") {
                                if (skillResource.questionType === "QUESTION") {
                                    newSkillResource.skills.questions = {
                                        type: "questions",
                                        name: skillResource.resourceSubNumbers,
                                        conceptId: skillResource.conceptId,
                                        tabHash: skillResource.tabHash
                                    };
                                } else if (skillResource.questionType === "WALKTHROUGH") {
                                    newSkillResource.skills.walkthroughs = {
                                        type: "walkthroughs",
                                        name: skillResource.resourceSubNumbers,
                                        conceptId: skillResource.conceptId,
                                        tabHash: skillResource.tabHash
                                    };
                                }
                            }
                            if (skillResource.resourceType === "TUTORIAL" && skillResource.tutorialSubType === "EXAMPLE") {
                                newSkillResource.skills.examples = {
                                    type: "examples",
                                    conceptId: skillResource.conceptId,
                                    tutorialId: skillResource.tutorialId,
                                    mediaName: skillResource.mediaName
                                };
                                newSkillResource.skills.videos = {
                                    type: "videos",
                                    mediaId: skillResource.mediaId,
                                    name: skillResource.mediaName,
                                    conceptId: skillResource.conceptId
                                };
                            }
                        });
                        newSkillResources.push(newSkillResource);
                    });
                    setSkillResources(newSkillResources);
                }).finally(() => {
                    setLoading(false);
                });
        } else {
            setSkillResources([]);
            setLoading(false);
        }
    }, [user]);

    if (!user.testResultId) return <div className="ch-warning-text">No {brandConfig.diagnosticFeedbackName} selected</div>;

    return (
        <>
            <CircularLoader visible={isLoading} displayInElement={true} />
            {!isLoading && skillResources.length > 0 && (
                <>
                    <div className="ch-menuWidget-head">{brandConfig.diagnosticFeedbackName}</div>
                    {skillResources && skillResources.length > 1 && (
                        <>
                            <ButtonsGroup className="flexInline flexSpaceBetween margin--m">
                                <Button className="js-next" type="text" size="small" disabled={activeSkillResource == 0}
                                        onClick={() => handlePrevClick()}>&lt; Previous set</Button>
                                <Button className="js-prev" type="text" size="small"
                                        disabled={activeSkillResource == (skillResources.length - 1)}
                                        onClick={() => handleNextClick()}>Next set &gt;</Button>
                            </ButtonsGroup>
                            <hr/>
                        </>
                    )}
                    <div className="margin--m">
                        <div className="text-truncate margin-b--m">{skillResources[activeSkillResource].name}</div>
                        {skillResources[activeSkillResource].skills.textbook && (
                            <button className="ch-toDo-blk ch-toDo-blk-1"
                                 onClick={() => {
                                     loadCurrentConcept(skillResources[activeSkillResource].skills.textbook.conceptId, skillResources[activeSkillResource].skills.textbook.tabHash)
                                     setNavVisible(false)
                                 }}>
                                <p>Revisit this section:</p>
                                <p><span>{skillResources[activeSkillResource].skills.textbook.name}</span></p>
                            </button>
                        )}
                        {skillResources[activeSkillResource].skills.videos && (
                            <button className="ch-toDo-blk ch-toDo-blk-2"
                                 onClick={() => {
                                     openVideoDialog(skillResources[activeSkillResource].skills.videos.mediaId)
                                     setNavVisible(false);
                                 }}>
                                <p>Watch these worked example videos:</p>
                                <p><span>{skillResources[activeSkillResource].skills.videos.name}</span></p>
                            </button>
                        )}
                        {skillResources[activeSkillResource].skills.examples && (
                            <button className="ch-toDo-blk ch-toDo-blk-3"
                                    onClick={() => {
                                        const example = skillResources[activeSkillResource].skills.examples
                                        openExampleDialog(example.conceptId, example.tutorialId, null, example.mediaName)
                                        setNavVisible(false);
                                    }}>
                                <p>See how you go with these nlig:</p>
                                <p><span>Now You Try {skillResources[activeSkillResource].skills.videos.name.toLowerCase().replace('example','')}</span></p>
                            </button>
                        )}
                        {skillResources[activeSkillResource].skills.questions && (
                            <button className="ch-toDo-blk ch-toDo-blk-4"
                                 onClick={() => {
                                     loadCurrentConcept(skillResources[activeSkillResource].skills.questions.conceptId, skillResources[activeSkillResource].skills.questions.tabHash)
                                     setNavVisible(false);
                                 }}>
                                <p>Do these exercise questions:</p>
                                <p><span>{skillResources[activeSkillResource].skills.questions.name}</span></p>
                            </button>
                        )}
                        <ButtonsGroup className="flexCentre margin-t--m">
                            <Button onClick={() => handleViewAll()}>View All</Button>
                            <Button onClick={() => updateFollowUpWork(undefined)}>Unpin</Button>
                        </ButtonsGroup>
                    </div>
                </>
            )}
            {!skillResources.length || skillResources.length === 0 && (
                <>No skills to revise</>
            )}
        </>
    )
}

export default StudentDiagnosticMenuWidget;